/**
 * Keeping an application's sign-in alive across QAIM's pages.
 *
 * An application's sign-in arrives as /login?login_challenge=…. Everything
 * that follows — creating the account, confirming the email, a trip to a
 * social provider — has to end back on the sign-in page with that challenge,
 * or the person ends up signed in to QAIM but never returned to the
 * application. Between QAIM's own pages the challenge rides in the URL; when
 * Kratos drives the browser (social sign-in, the emailed verification link)
 * it rides in the Kratos flow's return_to, which the pages set to themselves.
 */
import { safeHttpUrl } from './brandingService';

/**
 * The sign-in page. With a challenge it finishes that application's sign-in,
 * and `screen=login` keeps it on sign-in even when the application asked to
 * open on account creation (the person chose "Sign in", or just signed up).
 */
export function signInPath(loginChallenge?: string | null, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams();
  if (loginChallenge) {
    params.set('login_challenge', loginChallenge);
    params.set('screen', 'login');
  }
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  const query = params.toString();
  return query ? `/login?${query}` : '/login';
}

/** Account creation, for the same application's sign-in. */
export function registrationPath(loginChallenge?: string | null): string {
  return loginChallenge
    ? `/registration?login_challenge=${encodeURIComponent(loginChallenge)}`
    : '/registration';
}

/** Email confirmation: the flow Kratos mailed a code for, and the sign-in to finish after. */
export function verificationPath(flowId?: string, loginChallenge?: string | null): string {
  const params = new URLSearchParams();
  if (flowId) params.set('flow', flowId);
  if (loginChallenge) params.set('login_challenge', loginChallenge);
  const query = params.toString();
  return query ? `/verification?${query}` : '/verification';
}

/** A path on this site as an absolute URL — Kratos's return_to must be one. */
export function absoluteUrl(path: string): string {
  return new URL(path, window.location.origin).href;
}

/** A URL on this site as a path; null for anything elsewhere (never an open redirect). */
export function sameOriginPath(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin ? `${parsed.pathname}${parsed.search}` : null;
  } catch {
    return null;
  }
}

/** The login_challenge in a flow's return_to (or any URL on this site). */
export function challengeIn(url: string | null | undefined): string | null {
  const path = sameOriginPath(url);
  return path ? new URL(path, window.location.origin).searchParams.get('login_challenge') : null;
}

// The application behind a challenge, for when the challenge expires
// (Hydra's ttl.login_consent_request) before the person finishes — typically
// while confirming their email. localStorage, not sessionStorage: the emailed
// link opens in a new tab. Keyed by challenge, so it only ever applies to the
// sign-in it came from.
const PENDING_APP_KEY = 'qaim.pendingApp';

export interface PendingApp {
  name?: string;
  url?: string;
}

export function rememberApp(challenge: string, client: { client_name?: string; app_url?: string }): void {
  try {
    localStorage.setItem(
      PENDING_APP_KEY,
      JSON.stringify({ challenge, name: client.client_name, url: client.app_url })
    );
  } catch {
    // storage unavailable: an expired request just can't offer the way back
  }
}

/** The application a (now expired) challenge belonged to, if this browser saw it. */
export function rememberedApp(challenge: string): PendingApp {
  try {
    const saved = JSON.parse(localStorage.getItem(PENDING_APP_KEY) ?? 'null') as
      | (PendingApp & { challenge?: string })
      | null;
    if (saved?.challenge !== challenge) return {};
    return {
      name: typeof saved.name === 'string' && saved.name ? saved.name : undefined,
      url: safeHttpUrl(saved.url) ?? undefined,
    };
  } catch {
    return {};
  }
}
